'use client';

import { DataCatalogType } from '@repo/api-gw-sdk';

import { Wizard } from '@/components/wizard/Wizard';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace as useGlobalWorkspace } from '@/contexts/useWorkspace';

import { AddCatalogFlow } from './AddCatalogFlow';
import {
  createDefaultState,
  type AddCatalogWizardState,
} from './addCatalogWizardState';

interface AddCatalogWizardPanelProps {
  workspaceId: string;
  workspaceRegion: string | null;
  onAbort: () => void;
  onCreated: () => void;
}

export function AddCatalogWizardPanel({
  workspaceId,
  workspaceRegion,
  onAbort,
  onCreated,
}: AddCatalogWizardPanelProps) {
  const dal = useDAL();
  const { snackbar } = useGlobalWorkspace();

  const initialState = createDefaultState(workspaceId, workspaceRegion);

  const save = async (state: AddCatalogWizardState): Promise<boolean> => {
    const connectorId =
      state.connectorSource?.mode === 'existing'
        ? state.connectorSource.connectorId
        : state.connectorSource?.mode === 'new'
          ? state.connectorSource.createdConnectorId
          : undefined;

    if (!connectorId) {
      snackbar.showErrorMessage('No connector selected');
      return false;
    }

    if (!state.connectorExternalId) {
      snackbar.showErrorMessage('Missing connector external ID');
      return false;
    }

    if (state.catalogType === 'ICEBERG_REST_CATALOG') {
      try {
        await dal.lakehouse.createDataCatalog(workspaceId, {
          connectorId,
          name: state.catalogName,
          catalogType: DataCatalogType.DATA_CATALOG_TYPE_ICEBERG_REST_CATALOG,
          icebergRestProperties: {
            uri: state.icebergUri,
            warehouse: state.icebergWarehouse,
            authType: state.icebergAuthType,
            credentialsSecretArn:
              state.icebergCredentialsSecretArn || undefined,
          },
        });
        onCreated();
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to create catalog';
        snackbar.showErrorMessage(message);
        return false;
      }
    }

    if (state.catalogType === 'BIGLAKE_CATALOG') {
      try {
        await dal.lakehouse.createDataCatalog(workspaceId, {
          connectorId,
          name: state.catalogName,
          catalogType: DataCatalogType.DATA_CATALOG_TYPE_BIGLAKE_CATALOG,
          biglakeProperties: {
            catalogId: state.biglakeCatalogId,
            location: state.biglakeLocation,
          },
        });
        onCreated();
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to create catalog';
        snackbar.showErrorMessage(message);
        return false;
      }
    }

    // Glue catalog
    try {
      await dal.lakehouse.createDataCatalog(workspaceId, {
        connectorId,
        name: state.catalogName,
        catalogType: DataCatalogType.DATA_CATALOG_TYPE_GLUE_CATALOG,
        glueProperties: { region: state.region },
      });
      onCreated();
      return true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to create catalog';
      snackbar.showErrorMessage(message);
      return false;
    }
  };

  return (
    <Wizard<AddCatalogWizardState>
      onAbort={onAbort}
      hideBackArrow={true}
      onFinish={save}
      flow={[AddCatalogFlow]}
      initialState={initialState}
      title='Add Data Catalog'
      stepperLabels={[
        'Catalog Type',
        'Add Connector',
        'Catalog Configuration',
        'Review',
      ]}
    />
  );
}
